import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { getAuthErrorMessage } from "../utils/errorHandling";

interface CustomLoginFormProps {
  redirectUrl: string;
  onToggleForm: () => void;
  showPasswordResetNotification?: boolean;
}

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters")
});

type LoginFormValues = z.infer<typeof loginSchema>;

export const CustomLoginForm = ({
  redirectUrl,
  onToggleForm,
  showPasswordResetNotification = false
}: CustomLoginFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  });

  const handleLogin = async (values: LoginFormValues) => {
    setIsLoading(true);
    setAuthError(null);

    try {
      console.log("Signing in with email/password", {
        redirectUrl,
        envVars: {
          supabaseUrl: !!import.meta.env.VITE_SUPABASE_URL,
          supabaseAnonKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY
        }
      });
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password
      });

      if (error) {
        console.error("Authentication error:", error);
        setAuthError(getAuthErrorMessage(error));
        throw error;
      }

      toast.success("Successfully signed in!");

      setTimeout(() => {
        if (window.location.pathname === '/auth') {
          console.log("Manual redirect after successful login");
          window.location.href = redirectUrl || '/settings';
        }
      }, 1000);
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return <div className="w-full auth-form">
      {authError && <div className="p-3 mb-4 text-sm text-red-400 bg-red-900/20 rounded-md">
          <p>{authError}</p>
        </div>}
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleLogin)} className="flex flex-col">
          <FormField control={form.control} name="email" render={({
          field
        }) => <FormItem className="space-y-0">
                <FormControl>
                  <Input id="email" placeholder="Your email address" type="email" autoComplete="email" disabled={isLoading} className="h-9 px-2 text-white border-zinc-700 focus:border-green-500 placeholder:text-zinc-400 bg-zinc-900" {...field} />
                </FormControl>
                <div className="h-4 mt-0.5 my-0">
                  <FormMessage className="text-xs text-red-400" />
                </div>
              </FormItem>} />
          
          <FormField control={form.control} name="password" render={({
          field
        }) => <FormItem className="space-y-0">
                <FormControl>
                  <Input id="password" placeholder="Your password" type="password" autoComplete="current-password" disabled={isLoading} className="h-9 px-2 text-white border-zinc-700 focus:border-green-500 placeholder:text-zinc-400 bg-zinc-900" {...field} />
                </FormControl>
                <div className="h-4 mt-0.5">
                  <FormMessage className="text-xs text-red-400" />
                </div>
              </FormItem>} />

          <Button 
            type="submit" 
            disabled={isLoading} 
            className="!w-full !h-9 !text-white !mt-2 !bg-emerald-600 hover:!bg-emerald-500"
          >
            {isLoading ? <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </> : "Sign In"}
          </Button>
        </form>
      </Form>

      <div className="text-center mt-3">
        <button type="button" onClick={onToggleForm} className="text-sm hover:underline text-green-400 hover:text-green-300" disabled={isLoading}>
          Forgot password? Reset it here
        </button>
      </div>
    </div>;
};
